import { useListViewContext } from '@app/core/list-view/context/ListViewContext';
import { Box } from '@mui/material';
import { GenericParser, useQueryState } from 'nuqs';
import { MessageDescriptor, useIntl } from 'react-intl';
import { AodRulesAutocomplete } from './AodRulesAutocomplete';

/**
 * Props for the AOD Rules filter component.
 */
export interface AodRulesFilterProps {
  /** The URL query parameter key */
  name: string;
  /** Message descriptor for the label */
  label: MessageDescriptor;
  /** Parser for reading/writing this filter value from/to URL (from FilterDefinition). Handles defaults via .withDefault(). */
  parser: GenericParser<any>;
  /** Whether the filter is active (should be shown even if empty) */
  isActive?: boolean;
  /** Callback when filter is cleared (e.g. to remove from active filters set for addable filters) */
  onRemove?: () => void;
  /** Custom styles */
  sx?: object;
}

/**
 * AOD Rules filter component with immediate update.
 * Writes go through ListView context (setFilterValue) so filter changes are bundled with a page reset.
 * Reads come from nuqs (useQueryState) for simplicity.
 */
export function AodRulesFilter({
  name,
  label,
  parser,
  isActive,
  onRemove,
  sx,
}: AodRulesFilterProps) {
  const { $t } = useIntl();
  const { setFilterValue } = useListViewContext();

  // Read-only: get current value from URL (parent's useQueryStates manages it)
  const [value] = useQueryState(name, parser);

  // If it's an addable filter with no value and not active, don't render it in the bar
  // (It lives in the "Add Filter" menu until selected). Permanent filters don't receive
  // isActive, so isActive is undefined and we never hide them.
  if (isActive === false && !value) {
    return null;
  }

  const handleChange = (ruleId: string | null) => {
    setFilterValue(name, ruleId);
    // If clearing the filter and onRemove is provided, call it
    if (!ruleId && onRemove) {
      onRemove();
    }
  };

  const translatedLabel = $t(label);

  return (
    <Box sx={{ minWidth: '30ch' }}>
      <AodRulesAutocomplete
        value={value || null}
        onChange={handleChange}
        label={translatedLabel}
        sx={[
          sx,
          {
            minWidth: '30ch',
          },
        ]}
      />
    </Box>
  );
}
